import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TopBar from "../sidebar/TopBar";
import { fetchData } from "../../../helper/ApiCall";
import { BASE_URL_IMAGE, END_POINT, METHODS } from "../../../helper/helper";

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const c = {
  bg: "#F7F8FC",
  surface: "#FFFFFF",
  border: "#E8ECF4",
  text: "#1A202C",
  textMuted: "#718096",
  textFaint: "#A0AEC0",
  accent: "#4F46E5",
  accentLight: "#EEF2FF",
  danger: "#DC2626",
  dangerLight: "#FEE2E2",
};

// ─── CONFIRM MODAL ────────────────────────────────────────────────────────────
const ConfirmModal = ({ title, message, onConfirm, onCancel }) => (
  <div style={{
    position: "fixed", inset: 0, zIndex: 1000,
    background: "rgba(15,23,42,0.45)", backdropFilter: "blur(4px)",
    display: "flex", alignItems: "center", justifyContent: "center",
  }}>
    <div style={{
      background: c.surface, borderRadius: "16px", padding: "28px 28px 24px",
      width: "100%", maxWidth: "380px", boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
      border: `1px solid ${c.border}`, animation: "popIn 0.2s ease",
    }}>
      <div style={{ width: 44, height: 44, borderRadius: "12px", background: c.dangerLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px", marginBottom: "16px" }}>🗑️</div>
      <div style={{ fontSize: "16px", fontWeight: 700, color: c.text, marginBottom: "8px" }}>{title}</div>
      <div style={{ fontSize: "13px", color: c.textMuted, lineHeight: "1.6", marginBottom: "24px" }}>{message}</div>
      <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
        <button onClick={onCancel} style={{ padding: "9px 18px", borderRadius: "8px", border: `1px solid ${c.border}`, background: "transparent", color: c.textMuted, fontSize: "13px", fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
          Cancel
        </button>
        <button onClick={onConfirm} style={{ padding: "9px 18px", borderRadius: "8px", border: "none", background: c.danger, color: "#fff", fontSize: "13px", fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
          Delete
        </button>
      </div>
    </div>
  </div>
);

// ─── SKELETON CARD ────────────────────────────────────────────────────────────
const SkeletonCard = () => (
  <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: "14px", overflow: "hidden" }}>
    <div style={{ height: "180px", background: "linear-gradient(90deg, #f0f4f8 25%, #e8edf2 50%, #f0f4f8 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.4s infinite" }} />
    <div style={{ padding: "16px", display: "flex", flexDirection: "column", gap: "10px" }}>
      <div style={{ height: "14px", borderRadius: "6px", background: "#f0f4f8", width: "75%", animation: "shimmer 1.4s infinite" }} />
      <div style={{ height: "11px", borderRadius: "6px", background: "#f0f4f8", width: "90%", animation: "shimmer 1.4s infinite" }} />
      <div style={{ height: "11px", borderRadius: "6px", background: "#f0f4f8", width: "60%", animation: "shimmer 1.4s infinite" }} />
    </div>
  </div>
);

// ─── BLOG CARD ────────────────────────────────────────────────────────────────
const BlogCard = ({ item, onEdit, onDelete }) => {
  const [imgErr, setImgErr] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div
      style={{
        background: c.surface, border: `1px solid ${c.border}`, borderRadius: "14px",
        overflow: "hidden", display: "flex", flexDirection: "column",
        transition: "box-shadow 0.2s, border-color 0.2s",
        position: "relative",
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 8px 24px rgba(79,70,229,0.1)"; e.currentTarget.style.borderColor = "#C7D2FE"; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = c.border; }}
    >
      {/* Image */}
      <div style={{ position: "relative", height: "180px", background: "linear-gradient(135deg, #EEF2FF, #E0E7FF)", flexShrink: 0, overflow: "hidden" }}>
        {item.image && !imgErr ? (
          <img
            src={`${BASE_URL_IMAGE}${item.image}`}
            alt={item.title}
            onError={() => setImgErr(true)}
            style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.3s ease" }}
            onMouseEnter={e => e.currentTarget.style.transform = "scale(1.04)"}
            onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
          />
        ) : (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "36px", opacity: 0.5 }}>📝</div>
        )}

        {/* Menu button */}
        <div style={{ position: "absolute", top: "10px", right: "10px" }}>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            style={{
              width: 30, height: 30, borderRadius: "8px", border: "none",
              background: "rgba(255,255,255,0.9)", backdropFilter: "blur(8px)",
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "16px", boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
            }}
          >⋯</button>
          {menuOpen && (
            <div style={{
              position: "absolute", top: "34px", right: 0, zIndex: 10,
              background: c.surface, border: `1px solid ${c.border}`, borderRadius: "10px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)", overflow: "hidden", minWidth: "130px",
            }}>
              <button
                onClick={() => { onEdit(); setMenuOpen(false); }}
                style={{ width: "100%", padding: "10px 14px", border: "none", background: "none", textAlign: "left", fontSize: "13px", color: c.text, cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", fontFamily: "inherit" }}
                onMouseEnter={e => e.currentTarget.style.background = c.accentLight}
                onMouseLeave={e => e.currentTarget.style.background = "none"}
              >
                ✏️ Edit
              </button>
              <div style={{ height: "1px", background: c.border }} />
              <button
                onClick={() => { onDelete(); setMenuOpen(false); }}
                style={{ width: "100%", padding: "10px 14px", border: "none", background: "none", textAlign: "left", fontSize: "13px", color: c.danger, cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", fontFamily: "inherit" }}
                onMouseEnter={e => e.currentTarget.style.background = c.dangerLight}
                onMouseLeave={e => e.currentTarget.style.background = "none"}
              >
                🗑️ Delete
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: "16px", display: "flex", flexDirection: "column", flex: 1 }}>
        <h3 style={{
          margin: "0 0 8px", fontSize: "14px", fontWeight: 700, color: c.text,
          lineHeight: "1.4", display: "-webkit-box", WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>{item.title}</h3>

       {item.desc && (
  <p style={{
    margin: "0 0 14px", fontSize: "12px", color: c.textMuted, lineHeight: "1.6",
    display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden",
    flex: 1,
  }}>
    {item.desc.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()}
  </p>
)}

        {/* Footer actions */}
        <div style={{ display: "flex", gap: "8px", paddingTop: "12px", borderTop: `1px solid ${c.border}`, marginTop: "auto" }}>
          <button
            onClick={onEdit}
            style={{
              flex: 1, padding: "8px", borderRadius: "8px", border: `1px solid ${c.border}`,
              background: "#F1F5FF", color: c.accent, fontSize: "12px", fontWeight: 600,
              cursor: "pointer", fontFamily: "inherit", transition: "background 0.15s",
            }}
            onMouseEnter={e => e.currentTarget.style.background = c.accentLight}
            onMouseLeave={e => e.currentTarget.style.background = "#F1F5FF"}
          >✏️ Edit</button>
          <button
            onClick={onDelete}
            style={{
              padding: "8px 12px", borderRadius: "8px", border: `1px solid #FECACA`,
              background: c.dangerLight, color: c.danger, fontSize: "12px", fontWeight: 600,
              cursor: "pointer", fontFamily: "inherit", transition: "background 0.15s",
            }}
            onMouseEnter={e => e.currentTarget.style.background = "#FECACA"}
            onMouseLeave={e => e.currentTarget.style.background = c.dangerLight}
          >🗑️</button>
        </div>
      </div>
    </div>
  );
};

// ─── MAIN ─────────────────────────────────────────────────────────────────────
const BlogList = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, title }

  const loadBlogs = async () => {
    setLoading(true);
    try {
      const result = await fetchData(END_POINT.GetBlog, METHODS.GET);
      setBlogs(result?.success ? result.data || [] : []);
    } catch { setBlogs([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadBlogs(); }, []);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await fetchData(END_POINT.DeleteBlog + deleteTarget.id, METHODS.GET);
    setDeleteTarget(null);
    loadBlogs();
  };

  const filtered = blogs.filter(b =>
    !search || b.title?.toLowerCase().includes(search.toLowerCase()) || b.desc?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ minHeight: "100vh", background: c.bg, fontFamily: "'Inter', system-ui, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        * { box-sizing: border-box; }
        @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
        @keyframes popIn { from { opacity: 0; transform: scale(0.95) translateY(8px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-thumb { background: #CBD5E0; border-radius: 10px; }
      `}</style>

      <TopBar />

      {deleteTarget && (
        <ConfirmModal
          title="Delete Blog Post"
          message={`Are you sure you want to delete "${deleteTarget.title}"? This action cannot be undone.`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      <div style={{ maxWidth: "1350px", margin: "0 auto", padding: "28px 24px" }}>

        {/* ── Header ── */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px", flexWrap: "wrap", gap: "12px" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "4px" }}>
              <div style={{ width: 36, height: 36, borderRadius: "10px", background: "linear-gradient(135deg, #4F46E5, #7C3AED)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "17px" }}>📝</div>
              <h1 style={{ margin: 0, fontSize: "22px", fontWeight: 700, color: c.text }}>Blog Posts</h1>
            </div>
            <p style={{ margin: 0, fontSize: "13px", color: c.textMuted, paddingLeft: "46px" }}>
              {blogs.length} post{blogs.length !== 1 ? "s" : ""} published
            </p>
          </div>
          <button
            onClick={() => navigate("/admin/blogs/create")}
            style={{
              border: "none", background: c.accent, color: "#fff",
              padding: "10px 20px", borderRadius: "10px", cursor: "pointer",
              fontSize: "13px", fontWeight: 600, fontFamily: "inherit",
              display: "flex", alignItems: "center", gap: "6px",
              boxShadow: "0 4px 12px rgba(79,70,229,0.3)", transition: "all 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "#4338CA"; e.currentTarget.style.boxShadow = "0 6px 16px rgba(79,70,229,0.4)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = c.accent; e.currentTarget.style.boxShadow = "0 4px 12px rgba(79,70,229,0.3)"; }}
          >
            <span style={{ fontSize: "16px" }}>+</span> Create Blog
          </button>
        </div>

        {/* ── Search ── */}
        <div style={{
          background: c.surface, border: `1px solid ${c.border}`, borderRadius: "12px",
          padding: "12px 14px", marginBottom: "20px", display: "flex", alignItems: "center", gap: "10px",
        }}>
          <span style={{ fontSize: "16px", color: c.textFaint }}>🔍</span>
          <input
            placeholder="Search blog posts…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              flex: 1, border: "none", outline: "none", fontSize: "13px",
              color: c.text, background: "transparent", fontFamily: "inherit",
            }}
          />
          {search && (
            <button onClick={() => setSearch("")} style={{ background: "none", border: "none", cursor: "pointer", color: c.textFaint, fontSize: "16px", lineHeight: 1, padding: 0 }}>×</button>
          )}
          <div style={{ fontSize: "12px", color: c.textFaint, flexShrink: 0 }}>
            {filtered.length} / {blogs.length}
          </div>
        </div>

        {/* ── Content ── */}
        {loading ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))", gap: "14px" }}>
            {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : filtered.length === 0 ? (
          <div style={{
            background: c.surface, border: `1px solid ${c.border}`, borderRadius: "16px",
            padding: "64px 24px", textAlign: "center",
          }}>
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>{blogs.length === 0 ? "📝" : "🔍"}</div>
            <div style={{ fontSize: "16px", fontWeight: 700, color: c.text, marginBottom: "6px" }}>
              {blogs.length === 0 ? "No blog posts yet" : "No posts match your search"}
            </div>
            <div style={{ fontSize: "13px", color: c.textMuted, marginBottom: "20px" }}>
              {blogs.length === 0 ? "Create your first blog post to get started." : "Try a different search term."}
            </div>
            {blogs.length === 0 && (
              <button
                onClick={() => navigate("/admin/blogs/create")}
                style={{ border: "none", background: c.accent, color: "#fff", padding: "10px 20px", borderRadius: "10px", cursor: "pointer", fontSize: "13px", fontWeight: 600, fontFamily: "inherit" }}
              >+ Create Blog</button>
            )}
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))", gap: "14px" }}>
            {filtered.map((item) => (
              <BlogCard
                key={item._id}
                item={item}
                onEdit={() => navigate(`/admin/blogs/edit/${item._id}`)}
                onDelete={() => setDeleteTarget({ id: item._id, title: item.title })}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogList;